import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { getEmojiProvider } from '@helpcenter/emoji-provider';
import { isForgeAppsInJSMPhase1Enabled, isUseHelpCenterRefresh } from '@helpcenter/feature-flags';
import { PortalCardClient } from '@helpcenter/smart-card';
import { di } from 'react-magnetic-di';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import { getFabricEmojiUrl } from 'state/selectors/env';
import { ElementRoot, ElementWidth } from 'view/common/element-root';
import { DefaultExtensionProvider, type ExtensionManifest } from '@atlaskit/editor-common/extensions';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { MediaClientProvider } from '@atlaskit/media-client-react';
import type { AuthProvider } from '@atlaskit/media-core';
import { xcss, media } from '@atlaskit/primitives';
import { ReactRenderer } from '@atlaskit/renderer';
import { parseAdf } from '@atlassian/help-center-common-util/adf';
import { useGetValidHelpCenterAuthProvider } from '@atlassian/help-center-common-util/help-center-auth-provider';
import type { editorViewRendererFragment$key } from './__generated__/editorViewRendererFragment.graphql';
import { withEditorExtensions } from './with-editor-extensions';

interface RichTextViewerProps {
    editorExtensions?: Promise<ExtensionManifest[]>;
    editorElement: editorViewRendererFragment$key;
    disableAllLinks?: boolean;
    fabricEmojiUrl: string;
}

const smartCardClient = new PortalCardClient();

const EditorViewerComponent: React.FC<RichTextViewerProps> = ({
    editorElement,
    disableAllLinks,
    editorExtensions,
    fabricEmojiUrl,
}) => {
    di(ProviderFactory, ReactRenderer, useFragment);
    const data = useFragment(
        graphql`
            fragment editorViewRendererFragment on HelpLayoutEditorElement {
                adf
                elementType @required(action: THROW) {
                    mediaConfig(parentAri: $helpCenterAri) @required(action: THROW) {
                        mediaCollectionName @required(action: THROW)
                        mediaToken @required(action: THROW)
                        mediaUrl @required(action: THROW)
                        asapIssuer @required(action: THROW)
                    }
                }
            }
        `,
        editorElement
    );
    const { adf } = data;

    const { mediaCollectionName, mediaToken, mediaUrl, asapIssuer } = data.elementType.mediaConfig;

    const authProvider: AuthProvider = useGetValidHelpCenterAuthProvider({
        asapIssuer,
        token: mediaToken,
        baseUrl: mediaUrl,
    }); // TODO: get expiry duration via graphql

    const mediaProvider: MediaProvider = React.useMemo(
        () => ({
            uploadParams: {
                collection: mediaCollectionName,
            },
            viewMediaClientConfig: {
                authProvider,
                useSha256ForUploads: true,
            },
        }),
        [authProvider, mediaCollectionName]
    );

    const providerFactory = React.useMemo(() => {
        return ProviderFactory.create({
            mediaProvider: Promise.resolve(mediaProvider),
            extensionProvider: editorExtensions && Promise.resolve(new DefaultExtensionProvider(editorExtensions)),
            emojiProvider: __SERVER__ ? undefined : getEmojiProvider(true, fabricEmojiUrl),
        });
    }, [editorExtensions, mediaProvider, fabricEmojiUrl]);

    const [rendererKey, setRendererKey] = useState('without-editor-extensions');

    useEffect(() => {
        async function setExtensionProvider() {
            const extensions = await editorExtensions;
            if (extensions && extensions.length > 0 && providerFactory) {
                providerFactory.removeProvider('extensionProvider');
                providerFactory.setProvider(
                    'extensionProvider',
                    Promise.resolve(new DefaultExtensionProvider(extensions))
                );
            }
        }
        if (editorExtensions) {
            setExtensionProvider()
                .then(() => {
                    setRendererKey('with-editor-extensions');
                })
                .catch(() => {
                    setRendererKey('editor-extension-error');
                });
        }
    }, [editorExtensions, providerFactory]);

    const customStyles = [];
    if (disableAllLinks) {
        customStyles.push(disableRootStyle);
    }
    if (isUseHelpCenterRefresh()) {
        customStyles.push(smallScreenPadding);
    }

    if (typeof adf === 'string')
        return (
            <ElementRoot elementWidth={ElementWidth.COMPACT} customStyles={customStyles}>
                <MediaClientProvider clientConfig={mediaProvider.viewMediaClientConfig}>
                    <SmartCardProvider client={smartCardClient}>
                        <ReactRenderer
                            key={rendererKey}
                            appearance={'full-width'}
                            document={parseAdf(adf)}
                            dataProviders={providerFactory}
                            media={{
                                allowLinking: true,
                                allowCaptions: true,
                            }}
                            allowAltTextOnImages
                            shouldOpenMediaViewer
                        />
                    </SmartCardProvider>
                </MediaClientProvider>
            </ElementRoot>
        );
    return null;
};

const selector = createSelector(getFabricEmojiUrl, (fabricEmojiUrl) => ({
    fabricEmojiUrl,
}));

const RichTextViewer = isForgeAppsInJSMPhase1Enabled()
    ? withEditorExtensions(EditorViewerComponent)
    : EditorViewerComponent;

export default connect(selector, {})(RichTextViewer);

const disableRootStyle = xcss({
    pointerEvents: 'none',
});

const smallScreenPadding = xcss({
    // @ts-ignore
    [media.below.sm]: {
        paddingInline: 'space.400',
    },
});
